import React from "react";
import { reject } from "lodash";
import {
  Table,
  Pager,
  CardNotification,
  Loading,
  Checkbox,
  TableColumn,
} from "@foris/avocado-suite";
import { Classroom } from "@models/ISchema";
import { Icon } from "@foris/avocado-icons";
import { getColumnCell, getLinkCell } from "@common/utils/table.util";
import { useTranslation } from "react-i18next";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import css from "./advanceTable.module.scss";

interface AdvanceTableProps {
  loading: boolean;
  data: Classroom[];
  size: number;
  total: number;
  page: number;
  selected: Classroom[];
  setSelected: (value: Classroom[]) => void;
  onPageChange: (page: number) => void;
}

const AdvanceTable: React.FC<AdvanceTableProps> = (props: AdvanceTableProps) => {
  const { loading, data, size, total, page, onPageChange, setSelected, selected } = props;
  const { t } = useTranslation();
  const { editorUrl } = useNavigationUrl();

  const renderCheck = (classroom: Classroom) => {
    const findSelected = selected?.find(value => value.id === classroom?.id);
    const onChange = () => {
      let cloneSelected = selected ? [...selected] : [];
      if (!findSelected) cloneSelected.push(classroom);
      if (findSelected) cloneSelected = reject(cloneSelected, value => value.id === classroom?.id);
      setSelected(cloneSelected);
    };

    return <Checkbox disabled={!classroom?.usable} checked={!!findSelected} onChange={onChange} />;
  };

  const columns = [
    {
      key: "id",
      header: "",
      accessor: "id",
      renderCell: (row: any) => renderCheck(row),
    },
    {
      key: "classroom",
      header: t("assignation-edit.advanced-search.classroom.table-columns.classroom"),
      renderCell: ({ id, code, name }: Classroom) =>
        getLinkCell(editorUrl("classroom", id), null, `${code}, ${name}`),
      // desktopOptions: {
      //   headCellStyles: {
      //     minWidth: "80px",
      //     width: "25%",
      //   },
      // },
    },
    {
      key: "building",
      header: t("assignation-edit.advanced-search.classroom.table-columns.building-name"),
      renderCell: (row: any) => getColumnCell(row?.building?.code ?? "-"),
    },
    {
      key: "campus",
      header: t("assignation-edit.advanced-search.classroom.table-columns.campus"),
      renderCell: (row: any) => getColumnCell(row?.building?.campus?.code ?? "-"),
    },
    {
      key: "floor",
      header: t("assignation-edit.advanced-search.classroom.table-columns.floor"),
      renderCell: row => getColumnCell(row?.floor ?? "-"),
    },
    {
      key: "type",
      header: t("assignation-edit.advanced-search.classroom.table-columns.type"),
      renderCell: (row: any) => getColumnCell(row?.classroomType?.code ?? "-"),
    },
    {
      key: "capacity",
      header: t("assignation-edit.advanced-search.classroom.table-columns.capacity"),
      renderCell: (row: any) => getColumnCell(row?.capacity ?? "-"),
    },
    {
      key: "usable",
      header: t("assignation-edit.advanced-search.classroom.table-columns.usable"),
      renderCell: (row: any) => <Icon name={!!row?.usable ? "check" : "close"} />,
    },
    {
      key: "usagePercentage",
      header: t("assignation-edit.advanced-search.classroom.table-columns.usage"),
      renderCell: (row: any) =>
        getColumnCell(row?.assignment ? `${row?.assignment?.usagePercentage}%` : "-"),
    },
    {
      key: "chairsOccupationPercentage",
      header: t("assignation-edit.advanced-search.classroom.table-columns.ocupation"),
      renderCell: (row: any) =>
        getColumnCell(row?.assignment ? `${row?.assignment?.chairsOccupationPercentage}%` : "-"),
    },
  ];

  if (!data && loading) return <Loading />;

  if (total === 0)
    return (
      <section className={css.cardState}>
        <CardNotification
          state="info"
          title={t("assignation-edit.advanced-search.notifications.empty-results.title")}
          outlined
        >
          {t("assignation-edit.advanced-search.notifications.empty-results.classroom-message")}
        </CardNotification>
      </section>
    );

  if (data)
    return (
      <div className={css.advanceTable}>
        {loading && <Loading />}

        <Table columns={columns as TableColumn[]} data={data} />
        <Pager size={size} total={total} page={page} onChange={onPageChange} />
      </div>
    );

  return null;
};

export default AdvanceTable;
